<script type="text/jsx">
  import { lineMixins } from '@/Mixins/Lines'
  import { searchMixins } from '@/Mixins/SearchMixins'
  import buildLabelString from '@/utils/helpers/BuildLabelString'
  import MoneyField from '@/components/InputComponents/MoneyField.vue'
  import { capitalizeFirstLetter } from '@/utils/helpers/StringOperations'
  import { mapGetters } from 'vuex'

  export default {
    components: { MoneyField },
    mixins: [ lineMixins, searchMixins ],
    props: {
      saveErr: { 'type': Boolean, 'default': false },
      entityType: { 'type': String, 'default': '' },
      line_number: { 'type': Number, 'default': 1 },
      fields: { 'type': Array, 'default': () => [] },
      isLastRow: { 'type': Boolean, 'default': false },
      readOnly: { 'type': Boolean, 'default': false },
      showCloseBtn: { 'type': Boolean, 'default': false },
      showReopenBtn: { 'type': Boolean, 'default': false },
      querySearch: { 'type': Function },
      hideDeleteBtn: { 'type' : Boolean, 'default': false },
      willBeDeleted: { 'type': Boolean, 'default': false },
      original: { 'type': Object, 'default': () => {} },
      totalLines: { 'type': Number },
      trxStatus: { 'type': String, 'default': null },
      linesContainDcs: { 'type': Boolean, 'default': false },
      fieldsToClear: { 'type': Array, 'default': () => [] },
      splits: { 'type': Number, 'default': null },
      splitBy: { 'type': Array, 'default': null },
      isSplittingLine: { 'type': Boolean, 'default': false },
      maxAmtAfterSplit: { 'type': Object, 'default': () => {} },
      lineAccounts: { 'type': Array, 'default': () => [] },
      hasEditPermission: { 'type': Boolean, 'default': true }
    },
    data () {
      return {
        line: {},
        originalLine: {},
        searchTimeout: null,
        suggestions: {},
        changes: {},
        preventEditingTypes: [ 'RELIEF', 'ADJUSTMENT', 'CLOSED' ],
        lineIsActive: false,
        requirementsNotMet: false,
        relievesOptions: []
      }
    },
    methods: {
      /**
       * Handles focus events
       * @param e   JS Focus Event
       */
      handleFocusEvent (e) {
        if (e.target && e.target.dataset && e.target.dataset.row) {
          this.setLineActiveStatus(e.target.dataset.row)
        }
      },
      /**
       * Handles blur events
       * @param e   JS Focus Event
       */
      handleBlurEvent (e) {
        if (!(e && e.target)) {
          return
        }
        this.setValidatedFieldBackgroundColor(e)
        const { name } = e.target
        if (name.includes('amount_decimal')) {
          if (this.entityType === 'po') {
            let remaining = 0
            if (['POSTED', 'CLOSED', 'VOIDED', 'DELETED'].includes(this.trxStatus)) {
              if (this.originalLine && this.originalLine.amount_decimal) {
                remaining = Number(this.line['amount_decimal']) - (Number(this.originalLine.amount_decimal) - Number(this.originalLine.remaining_decimal))
                if (remaining < 0) {
                  this.modifyLine('amount_decimal', this.originalLine.amount_decimal)
                  this.modifyLine('remaining_decimal', this.originalLine.remaining_decimal)
                  this.$notify({ title: 'Line amount cannot be lesser than relieved amount', type: 'warning' })
                  return false
                }
                this.modifyLine('remaining_decimal', remaining)
              } else {
                this.modifyLine('remaining_decimal', Number(this.line['amount_decimal']))
              }
            } else {
              this.modifyLine('remaining_decimal', Number(this.line['amount_decimal']))
            }
          }
          this.$emit('lineAmountChanged', this.line)
        }
        if (this.entityType === 'ar' && (name.includes('markup_amount') || name.includes('amount_decimal'))) {
          this.updateAmountWithMarkups()
        }
        if (e.relatedTarget && e.relatedTarget.dataset && e.relatedTarget.dataset.row) {
          this.setLineActiveStatus(e.relatedTarget.dataset.row)
        }
      },
      /**
       * Sets the matching suggestion if an autocomplete field's target element contains a value but the field is blurred
       * without the user having selected a suggestion. If the target element's value perfectly matches a suggestion, and
       * if only one suggestion is returned, then selects that value and sets it in the line's form object.
       * @param e   JS Focus Event
       */
      setSuggestionIfMatched (e) {
        try {
          const { fieldname } = e.target.dataset
          const field = this.fields.find(field => field.name === fieldname)
          if (field && typeof field.options === 'function') {
            const results = this.handleSearch(field.options(), e.target.value)
            if (results && results.length === 1) {
              const match = results[0]
              match.label = match.label.includes(' - ') ? match.label.split(' - ')[0] : match.label
              this.modifyLine(fieldname, match)
            }
          }
        } catch (err) {
          console.log(`Unable to set closest suggestion: ${err}`)
        }
      },
      /**
       * Handles a blur event emitted from an autocomplete component's <input> element. If the target element's value
       * property is '' or not equal to the value of the corresponding form field "label" property, then the form field
       * must be reset to { label: '', value: '' }. Also, if the target element's "value" property is not equal to the
       * corresponding form field, then the cell must be highlighted in red, because it indicates that the user entered
       * invalid data into the cell *after* having made a valid suggestion.
       * @param e   Blur event
       */
      async handleAutocompleteBlur (e) {
        const { dataset } = e.target
        if (dataset && e.target.value === '') {
          this.modifyLine(dataset.fieldname, { label: '', value: '' })
        } else if (dataset && e.target.value && !this.line[dataset.fieldname].value) {
          this.setSuggestionIfMatched(e)
        }
        await new Promise(() => setTimeout(() => {
          if (dataset && e.target.value !== this.line[dataset.fieldname].label) {
            this.modifyLine(dataset.fieldname, { label: '', value: '' })
          }
          this.handleBlurEvent(e)
          return Promise.resolve()
        }, 100))
      },
      /**
       * Determines whether or not this line is currently active, meaning that one of its fields has focus
       * @param row   The line's "row" value obtained from the active element's dataset object
       */
      setLineActiveStatus (row) {
        this.lineIsActive = String(row) === String(this.line_number)
      },
      /**
       * Determines whether the line is complete when the line is no longer active
       * @param e   JS Focus Event
       */
      checkIfLineIsComplete (e) {
        if (this.lineIsActive) {
          this.requirementsNotMet = false
        } else if (!this.lineIsActive && String(e.target.dataset.row) === String(this.line_number)) {
          const requiredFields = this.fields.filter(field => field.required)
          const preFilled = ['line_number', 'company', 'production', 'series', 'location', 'set', 'asset']
          const atLeastOneFieldHasBeenFilled = this.fields
            .filter(field => !preFilled.includes(field.name))
            .some(field => this.checkFormFieldForValue(this.line[field.name]))
          const allRequiredFieldsHaveBeenFilled = requiredFields
            .every(field => this.checkFormFieldForValue(this.line[field.name]))
          this.requirementsNotMet = atLeastOneFieldHasBeenFilled && !allRequiredFieldsHaveBeenFilled
        }
        this.$emit('requirementsNotMet', { line: this.line_number, requirementsNotMet: this.requirementsNotMet })
      },
      /**
       * Checks whether a field has a value
       * @param formField   The form field
       */
      checkFormFieldForValue (formField) {
        if (formField && formField.hasOwnProperty('value')) {
          return !!formField.value
        } else if (formField && !formField.hasOwnProperty('value')) {
          return typeof formField !== 'undefined' && formField !== '' || typeof formField === 'boolean'
        }
        return false
      },
      /**
       * Sets a form field's background color depending on whether or not that field has a value
       * @param e   JS Focus Event
       */
      async setValidatedFieldBackgroundColor (e) {
        const { fieldname } = e.target.dataset
        const field = this.fields.find(field => field.name === fieldname)
        const formField = this.line[fieldname]
        await new Promise(() => {
          setTimeout(() => {
            if (e.target && !e.target.value && field && !field.required) {
              return
            }
            e.target.style.background = !this.checkFormFieldForValue(formField) ?
              '#ff49493d' :
              (field.disabled ? '#eef1f6' : '#fff')
          })
        })
      },
      /**
       * Updates this line's amount_with_markups_decimal field to reflect the new amount after markups have been applied,
       * based on the value of markup_type and markup_amount_decimal or markup_rate.
       */
      updateAmountWithMarkups () {
        const mType = this.line.markup_type.value || this.line.markup_type
        const { markup_amount, amount_decimal } = this.line
        let newAmt
        switch (mType) {
          case 'AMT':
            newAmt = amount_decimal && markup_amount
              ? (parseFloat(amount_decimal) + parseFloat(markup_amount))
              : parseFloat(amount_decimal)
            break
          case 'PCT':
            newAmt = amount_decimal && markup_amount
              ? (parseFloat(amount_decimal) + (parseFloat(amount_decimal) * (parseFloat(markup_amount) / 100)))
              : parseFloat(amount_decimal)
            break
          default:
            newAmt = amount_decimal
        }
        this.modifyLine('amount_with_markups_decimal', newAmt)
      },
      /**
       * Handles selection events for autocomplete and select fields
       * @param selection   The autocomplete/select field  selection
       * @param fieldName   The name of the field from which the selection originates
       */
      handleSelect (selection, fieldName) {
        let sel = selection
        sel.label = sel.label.includes(' - ') ? sel.label.split(' - ')[0] : sel.label
        this.modifyLine(fieldName, sel)
        const field = this.fields.find(field => field.name === fieldName)
        if (field && (field.fieldType === 'select' || field.fieldType === 'autocomplete') && sel && sel.value) {
          if (field.remote) {
            this.cacheSelectionResults(fieldName)
          }
          if (field.name === 'relieves') {
            this.fillFieldsWithPoLineData(sel.value)
          }
        }
        if (this.entityType === 'ar') {
          if (fieldName === 'markup_type') {    // Update the 'amount_with_markups' field
            this.updateAmountWithMarkups()
          }
          if (fieldName === 'item' && (!this.line.markup_amount)) {   // Set inherited markup
            this.$emit('itemFieldChanged', { lineNumber: this.line_number, item: this.line.item.value })
          }
        }
      },
      /**
       * Caches the selected search result for autocomplete fields
       * @param fieldName   The name of the field for which to cache the selected search result
       */
      async cacheSelectionResults (fieldName) {
        let storeActionPayload
        switch (fieldName) {
          case 'relieves':
            storeActionPayload = { entityType: 'lines/po', mutation: 'RECEIVE_PO_LINES' }
            break
          case 'third_party_vendor':
            storeActionPayload = { entityType: 'vendors', mutation: 'RECEIVE_VENDORS' }
            break
          case 'bill':
            storeActionPayload = { entityType: 'ars', mutation: 'RECEIVE_AR' }
            break
          case 'prepayment':
            storeActionPayload = { entityType: 'pps', mutation: 'RECEIVE_PP' }
        }
        await this.$store.dispatch('storeLastSearchResults', storeActionPayload)
      },
      /**
       * Automatically fills a line's fields with data from a selected PO line
       * @param poLineId
       */
      async fillFieldsWithPoLineData (poLineId) {
        let poLine = this.getPoLineById(poLineId)
        if(!poLine.id){
          await this.$store.dispatch('fetchPoLinesWithQuery', {id: poLineId})
          poLine = this.getPoLineById(poLineId)
        }
        let lineObj = {}
        lineObj[poLine.id] = poLine
        try {
          const promises = await this.$store.dispatch('findDependencies', { lines: lineObj })
          await Promise.all(promises)
        } catch (e) {
          console.log(e)
        }

        // Empty free fields
        this.line['free_field_1'] = { label: '', value: '' }
        this.line['free_field_2'] = { label: '', value: '' }
        this.line['free_field_3'] = { label: '', value: '' }
        this.line['free_field_4'] = { label: '', value: '' }
        this.line['third_party_vendor'] = { label: '', value: '' }
        this.line['tax_code'] = { label: '', value: '' }
        this.line['asset'] = false
        this.line['is_offset'] = false

        for (let f in this.line) {
          const skipAmountDecimalField = (f === 'amount_decimal' && ['POSTED', 'CLOSED', 'VOIDED', 'DELETED'].includes(this.trxStatus))
          if (!(Object.keys(poLine).includes(f) && poLine[f]) || skipAmountDecimalField) continue
          let val
          switch (f) {
            case 'amount_decimal':
              if (this.originalLine.relieves && Number(this.originalLine.relieves.value) === Number(poLineId)) {
                val = poLine['amount_decimal'] || poLine[f]
              } else {
                val = poLine['remaining_decimal'] || poLine[f]
              }
              break
            case 'description':
              val = poLine['description']
              break
            case 'account':
              val = this.$store.getters[`get${f.charAt(0).toUpperCase() + f.slice(1)}ById`](poLine[f])
              if (!val.id) {
                await this.$store.dispatch('fetchAccountById', poLine[f])
                if (poLine[f] && poLine.account_code) {
                  val = { code: poLine.account_code, value: poLine[f] }
                } else {
                  const poLineAccount = this.getAccountById(poLine[f])
                  val = { code: poLineAccount.account_code, value: poLine[f] }
                }
              }
              break
            case 'location':
            case 'series':
            case 'set':
              val = this.$store.getters[`get${f.charAt(0).toUpperCase() + f.slice(1)}ById`](poLine[f])
              break
            case 'free_field_1':
            case 'free_field_2':
            case 'free_field_3':
            case 'free_field_4':
              val = this.getFreefieldById(poLine[f])
              break
            case 'tax_code':
              val = this.getTaxcodeById(poLine[f])
              break
            case 'third_party_vendor':
              val = this.getVendorById(poLine[f])
              break
            case 'other_name':
              val = poLine['other_name']
              break
          }
          if (val && this.line[f].hasOwnProperty('value')) {
            this.line[f].label = val.code || val.name
            this.line[f].value = poLine[f]
          } else if (val && !this.line[f].hasOwnProperty('value')) {
            if (f === 'account') {
              this.line[f].label = val.code || val.name
              this.line[f].value = poLine[f]
            } else {
              this.line[f] = val
            }
          } 
          for (let ref in this.$refs) { // Manually set cell color to valid
            if (ref.includes(f) && ref.includes('autocomplete')) {
              let nested = this.$refs[ref].$el.getElementsByTagName('input')[0]
              nested.style.background = 'none'
            }
          }
          this.trackChanges(f)
        }
        return true
      },
      /**
       * Handles input events for input fields
       * @param fieldName   The name of the field from which the input originates
       * @param value
       */
      handleInput (fieldName, value) {
        let name = fieldName.includes('__') ? fieldName.split('__')[1] : fieldName
        if (['amount_decimal', 'markup_amount', 'amount_with_markups_decimal'].includes(name)) {
          this.line[name] = value
        }
        this.trackChanges(name)
        if (this.splits && name === 'amount_decimal') {
          this.$emit('updateMaxAmtAfterSplit', this.splits, this.line_number)
        }
      },
      /**
       * Modifies the actual line form, ensuring it works accordingly with Vue's reactivity system
       * @param fieldName   The name of the field to modify
       * @param newValue    The new value of the field to modify
       */
      modifyLine (fieldName, newValue) {
        this.$emit('lineModified', { lineNumber: this.line_number, fieldName, newValue, instance: this })
        if (this.line.hasOwnProperty(fieldName)) {
          let tmp = this.line
          tmp[fieldName] = newValue
          this.line = tmp
          this.trackChanges(fieldName)
        }
      },
      /**
       * Tracks changes to this line
       * @param fieldName   The name of the field for which to check for changes
       */
      trackChanges (fieldName) {
        const oldVal = this.originalLine.hasOwnProperty(fieldName) ? this.originalLine[fieldName] : ''
        const newVal = this.line[fieldName]
        const compare = (newValue, oldValue = '') => {
          let tmpChanges = this.changes
          if (newValue !== oldValue) {
            tmpChanges[fieldName] = newVal
          } else {
            delete tmpChanges[fieldName]
          }
          this.changes = tmpChanges
        }
        if (newVal && newVal.hasOwnProperty('value')) {
          compare(newVal.value, oldVal.value)
        } else {
          compare(newVal, oldVal)
        }
        this.$emit('lineChanged', { num: this.line_number, form: this.line })
      },
      /**
       * Formats fields that show currencies
       * @param data
       */
      formatMoneyFields (data) {
        this.$emit('formatMoneyFields', data)
      },
      /**
       * Handles autocomplete searches
       * @param queryOpts
       * @param cb
       */
      querySearchAsync ({ query, fieldName, opts }, cb) {
        if (!(query && query !== '' && query.length >= 1)) {
          return
        }
        let queryObj = {}
        queryObj['search'] = query
        opts.query = Object.assign(opts.query, queryObj)
        this.suggestions[fieldName] = []
        let results = this.suggestions[fieldName].filter(v => v.value.indexOf(query.toLowerCase()) === 0)
        if (this.searchTimeout) clearTimeout(this.searchTimeout)
        this.searchTimeout = setTimeout(() => {
          return this.querySearch(opts)
            .then(r => {
              if (!(r && r.entities && r.entities[opts.endpoint])) return cb(results)
              results = Object.values(r.entities[opts.endpoint]).map(v => {
                return { label: buildLabelString(v), value: v.id }
              })
              cb(results)
            })
            .then(() => this.highlightFirstAutocompleteResult())
            .catch(e => console.log(e))
        }, 400)
      },

      /**
       * Highlights the first autocomplete result after search
       */
      highlightFirstAutocompleteResult (ref = null) {
        const { $refs } = this
        const highlight = (r) => {
          if (r.includes('autocomplete') && $refs[r] && $refs[r].suggestions && $refs[r].suggestions.length) {
            this.$refs[r].highlight(0)
          }
        }
        if (ref) {
          highlight(ref)
        } else {
          for (let r in $refs) {
            highlight(r)
          }
        }
      },
      /**
       * Generates an HTML <input> field of type autocomplete where remote is true
       * @param field
       * @param fieldIndex
       * @param inputName
       * @return {XML}
       */
      generateAutocompleteField (field, fieldIndex, inputName) {
        const disabled = field.readOnly || (this.splitBy && this.splitBy.length) || this.isOffsetLine(this.line) || !this.hasEditPermission
        const defaultSearchOpts = { endpoint: field.name, queryObj: {} }
        const ref = `line_${this.line_number}_autocomplete_${field.name}`
        return (
          <el-autocomplete
            placeholder="---"
            value={this.line[field.name].label}
            popper-class="suggestion-list"
            onSelect={(selection) => this.handleSelect(selection, field.name)}
            data-row={this.line_number}
            data-col={fieldIndex}
            data-lastcell={fieldIndex === this.fields.length - 1}
            data-lastrow={this.isLastRow}
            data-fieldname={field.name}
            disabled={disabled}
            fetch-suggestions={(query, cb) => this.querySearchAsync({
              query,
              fieldName: field.name,
              opts: (field.searchOpts || defaultSearchOpts)
            }, cb)}
            props={{ label: 'label', value: 'value' }}
            trigger-on-focus={false}
            name={inputName}
            ref={ref}>
          </el-autocomplete>
        )
      },
      /**
       * Generates an HTML <input> field of type autocomplete where remote is false
       * @param field
       * @param fieldIndex
       * @param inputName
       * @return {XML}
       */
      generateLocalAutocompleteField (field, fieldIndex, inputName) {
        const disabled = field.readOnly || (this.splitBy && this.splitBy.length) || this.isOffsetLine(this.line) || !this.hasEditPermission
        const ref = `line_${this.line_number}_autocomplete_${field.name}`
        return (
          <el-autocomplete
            placeholder="---"
            onSelect={(selection) => this.handleSelect(selection, field.name)}
            value={this.line[field.name].label}
            popper-class="suggestion-list"
            data-row={this.line_number}
            data-col={fieldIndex}
            data-lastcell={fieldIndex === this.fields.length - 1}
            data-lastrow={this.isLastRow}
            data-fieldname={field.name}
            disabled={disabled}
            fetch-suggestions={(query, cb) => this.autocompleteLocalSearch({ query, ref, field }, cb)}
            props={{ label: 'label', value: 'value' }}
            trigger-on-focus={false}
            name={inputName}
            ref={ref}>
          </el-autocomplete>
        )
      },
      /**
       * Generates an HTML <select> field
       * @param field
       * @param fieldIndex
       * @param inputName
       * @return {XML}
       */
      generateSelectField (field, fieldIndex, inputName) {
        if (this.entityType === 'ap') {
          if (field.name === 'relieves') {
            // if (this.relievesOptions.length === 0) {
            //   this.relievesOptions = field.options
            // }

            if (!this.line.id) {
              field.options = field.options.filter((option) => option.type !== 'TRANSFERRED')
            }
          }

          if (this.line.id && this.originalLine.relieves && this.originalLine.relieves.value) {
            // field.options = this.relievesOptions.filter((option) => option.type !== 'TRANSFERRED')
            let poCheck = field.options.find(opt => opt.id === this.originalLine.relieves.value)
            if (!poCheck) {
              let poLine = this.getPoLineById(this.originalLine.relieves.value)
              field.options.push(poLine)
            }
          }
        }
        
        const { line_number, entityType, handleSelect } = this
        const { name, options, readOnly } = field
        const getSelectOpts = options.map((opt, i) => {
          if ((!['pc', 'je'].includes(entityType) && name === 'relieves') || opt.meta_name) {
            return <el-option key={`${opt.id}_${Math.floor(Math.random() * 10000)}`} label={opt.meta_name} value={{ value: opt.id, label: opt.meta_name }} />
          }
          if (entityType === 'ar' && name === 'markup_type') {
            return <el-option key={`${i}_${Math.floor(Math.random() * 10000)}`} label={opt.label} value={{ value: opt.value, label: opt.label }} />
          }
          if (!opt.meta_name && opt.code && opt.name) {
            const label = `${opt.code} - ${opt.name}`
            return <el-option key={`${i}_${Math.floor(Math.random() * 10000)}`} label={label} value={{ value: opt.id, label }} />
          }
          return <el-option key={`${opt.id}_${Math.floor(Math.random() * 10000)}`} label={opt.meta_name} value={{ value: opt.id, label: opt.name }} />
        })
        const getPoSelectOpts = options.map((opt, i) => {
          return <option key={`${opt.id}_${Math.floor(Math.random() * 10000)}`} label={opt.meta_name} value={ opt.id } />
        })
        // const shouldDisable = () => (readOnly || (name === 'relieves' && !options.length))
        const disabled = readOnly || (name === 'relieves' && !options.length)
        if(name === 'relieves'){
          return (
            <select
              key={`${name}_${this.line_number}_${Math.floor(Math.random() * 10000)}`}
              v-model={this.line[name].value}
              data-row={this.line_number}
              data-col={fieldIndex}
              data-fieldtype="select"
              data-fieldname={name}
              style="width: 100%; height: 100%; border: none;"
              name={inputName}
              disabled={disabled}
              onInput={(e) => handleSelect({value: e.target.value, label: e.target[e.target.selectedIndex].label}, field.name)}
              placeholder={field.placeholder || 'Select option'}
              ref={`line_${line_number}_select_${field.name}`}>
              { getPoSelectOpts }
            </select>
          )
        } else {
          return (
            <el-select
              key={`${name}_${this.line_number}_${Math.floor(Math.random() * 10000)}`}
              data-row={this.line_number}
              data-col={fieldIndex}
              data-fieldtype="select"
              data-fieldname={name}
              style="width: 100%;"
              name={inputName}
              v-model={this.line[name]}
              disabled={disabled}
              filterable
              placeholder={field.placeholder || 'Select option'}
              onInput={(e) => handleSelect(e, field.name)}
              ref={`line_${line_number}_select_${field.name}`}>
              { getSelectOpts }
            </el-select>
          )
        }
      },
      /**
       * Generates a simple display field which does nothing but display a preset value
       * @param field
       * @return {XML}
       */
      generateDisplayField (field) {
        let fieldName = field.name.includes(':') ? field.name.split(':')[0] : field.name
        const style = {
          maxWidth: '200px',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          color: '#333333',
          cursor: 'not-allowed'
        }
        let value = null
        if (typeof field.displayVal === 'function') {
          value = field.displayVal(this.line[fieldName].value)
          if (!value) {
            const lineAccount = this.lineAccounts.find(v => {
              return v.id === this.line[fieldName].value
            })
            value = lineAccount && lineAccount.name ? lineAccount.name : ''
          }
        } else {
          value = field.displayVal
        }
        return (
          <div style={style}>
            <span style={ { fontSize: '12px', paddingLeft: '10px' } }>
              { value }
            </span>
          </div>
        )
      },
      /**
       * Generates an HTML <input> field
       * @param field
       * @param fieldIndex
       * @param inputName
       * @return {XML}
       */
      generateInputField (field, fieldIndex, inputName) {
        const { line_number, handleInput, handleBlurEvent, handleFocusEvent } = this
        const disabled = field.readOnly || !!(this.splitBy && this.splitBy.length) || this.isOffsetLine(this.line)
        const style = {
          'width': '99%',
          'border-radius': '5px',
          'color': disabled ? '#333333' : 'black',
          'background': disabled ? '#eef1f6' : 'none',
          'cursor': disabled ? 'not-allowed' : 'initial',
          'text-align': field.name === 'amount_decimal' ? 'right' : 'initial'
        }
        return (
          <input
            autocomplete="off"
            data-row={this.line_number}
            data-col={fieldIndex}
            data-fieldtype="input"
            data-fieldname={field.name}
            style={style}
            name={inputName}
            onBlur={handleBlurEvent}
            onFocus={handleFocusEvent}
            title={this.line[field.name]}
            v-model={this.line[field.name]}
            onInput={() => handleInput(field.name)}
            disabled={disabled}
            ref={`line_${line_number}_input_${field.name}`}
          />
        )
      },
      /**
       * Generates a custom MoneyField that formats dollar amounts
       * @param field
       * @param fieldIndex
       * @param inputName
       * @return {XML}
       */
      generateMoneyField (field, fieldIndex, inputName) {
        const { line_number, handleInput, handleBlurEvent, handleFocusEvent, line } = this
        const disabled = (field.readOnly && !this.splits) ||
          (this.entityType === 'po' && this.preventEditingTypes.includes(line.type)) ||
          this.isSplittingLine || !this.hasEditPermission
        return (
          <MoneyField
            dataoptions={{ 'data-row': this.line_number, 'data-col': fieldIndex, 'data-fieldtype': 'input', 'data-fieldname': field.name }}
            name={inputName}
            currency={field.currency}
            disabled={disabled}
            reference={`line_${line_number}_input_${field.name}`}
            ref={`line_${line_number}_input_${field.name}`}
            value={this.line[field.name]}
            onBlur={handleBlurEvent}
            onFocus={handleFocusEvent}
            onInput={(value) => handleInput(field.name, value)}
            style="text-align: right;"
          />
        )
      },
      /**
       * Generates an HTML <input> field of type checkbox
       * @param field
       * @param fieldIndex
       * @param inputName
       * @return {XML}
       */
      generateCheckboxField (field, fieldIndex, inputName) {
        const { line_number, handleInput, handleBlurEvent, handleFocusEvent } = this
        const disabled = field.readOnly || !!(this.splitBy && this.splitBy.length) || this.isOffsetLine(this.line)
        const style = {
          'cursor': disabled ? 'not-allowed' : 'initial'
        }
        return (
          <input
            data-row={this.line_number}
            data-col={fieldIndex}
            data-fieldtype="checkbox"
            data-fieldname={field.name}
            type="checkbox"
            name={inputName}
            v-model={this.line[field.name]}
            onChange={() => handleInput(field.name)}
            onBlur={handleBlurEvent}
            onFocus={handleFocusEvent}
            disabled={disabled}
            style={style}
            ref={`line_${line_number}_checkbox_${field.name}`}
          />
        )
      },
      /**
       * Gets the right HTML input element based on the specified field type
       * @param field
       * @param fieldIndex
       * @return {*}
       */
      getField (field, fieldIndex) {
        const { line, line_number, readOnly, entityType, showCloseBtn, showReopenBtn } = this
        const inputName = `line${line_number}__${field.name}`
        switch (field.fieldType) {
          case 'display':
            return this.generateDisplayField(field)
          case 'select':
            return this.generateSelectField(field, fieldIndex, inputName)
          case 'autocomplete':
            if (field.remote) {
              return this.generateAutocompleteField(field, fieldIndex, inputName)
            }
            return this.generateLocalAutocompleteField(field, fieldIndex, inputName)
          case 'input':
            return this.generateInputField(field, fieldIndex, inputName)
          case 'money':
            return this.generateMoneyField(field, fieldIndex, inputName)
          case 'checkbox':
            return this.generateCheckboxField(field, fieldIndex, inputName)
          case 'button':
            if (entityType === 'po' && showCloseBtn && ['OPEN', 'PARTIAL'].includes(line.type) && line.id) {
              return (
                <div style={{ textAlign: 'center' }}>
                  <el-button disabled={!this.hasEditPermission} onClick={() => this.$emit('closeLine', line.close_reopen_id)} size="mini">
                    Close
                  </el-button>
                </div>
              )
            } else if (entityType === 'po' && ['CLOSED'].includes(line.type) && ['Manual'].includes(line.source_type)) {
              return (
                <div style={{ textAlign: 'center' }}>
                  <el-button disabled={readOnly} onClick={() => this.$emit('reopenline', line.close_reopen_id)} size="mini">
                    Reopen
                  </el-button>
                </div>
              )
            }
        }
      },
      /**
       * Generates the fields that make up a distribution line
       */
      generateLineFields () {
        return this.fields.filter(f => !f.hidden).map((field, i) => {
          if (!(field && field.name && field.fieldType)) return (<div>Error</div>)
          if (field.name === 'line_number') {
            return (
              <td className="line-num" style={`text-align: center; background: ${this.requirementsNotMet ? '#f7ba2a75' : 'none'}`}>
                { this.line_number }
              </td>
            )
          }
          const setStyle = () => {
            let style = {
              'text-align': `${field.name === 'asset' ? 'center' : 'left'}`,
              'border': '1px solid lightgrey',
              'height': '28px',
            }

            // Handle bad account coding issue #1402
            if (field.name === 'account:name' && this.line && this.line.account && this.line.account.label && this.line.account.label.match(/^X/i)) {
              style['background-color'] = '#ff5a3670';
              style['color'] = '#ff5a36';
            } else if (this.isOffsetLine(this.line) || !this.hasEditPermission) {
              style['cursor'] = 'not-allowed'
              style['background-color'] = '#eef1f6';
            } else {
              style['background-color'] = (field.displayVal || field.readOnly) ? '#eef1f6' : ''
            }

            return style
          }
          return (
            <td className="data-row" key={`${field.name}_${i}_container`} width={field.fieldWidth} style={setStyle()}>
              { this.getField(field, i) }
            </td>
          )
        })
      },
      /**
       * Initializes the line form
       */
      initializeLineForm () {
        this.fields.forEach(field => {
          let originalVal = null
          if (Object.keys(this.original) && this.original.hasOwnProperty(field.name)) {
            originalVal = Object.assign({}, this.original)[field.name]
          }
          switch (field.fieldType) {
            case 'select':
            case 'input':
            case 'money':
              this.line[field.name] = originalVal || (field.defaultValue || '')
              break
            case 'checkbox':
              this.line[field.name] = originalVal || (field.defaultValue || false)
              break
            case 'autocomplete':
              this.line[field.name] = originalVal || (field.defaultValue || { label: '', value: '' })
          }
        })
        if (this.original && this.original.id) {
          this.line['id'] = this.original.id
        }
      },
      /**
       * Initializes the component, attaching necessary event listeners and attributes to DOM elements after
       * rendering. This needs to be done after render, because certain Element UI elements such as select
       * and autocomplete nest their actual <input> elements within parent <div>s, and registering event listeners
       * to the actual Element UI component (before render) will bind them to the container elements,
       * no the actual input fields, which prevents things such as being able to .focus() on an autocomplete input
       * using the keyboard, etc.
       */
      initializeComponent () {
        const { handleAutocompleteBlur, handleFocusEvent } = this
        for (let r in this.$refs) {
          const ref = this.$refs[r]
          let el = ref.$el ? ref.$el : ref
          const isAutocomplete = r.includes('autocomplete')
          const isSelect = r.includes('select')
          if (el && el.dataset && el.dataset.row && el.dataset.col && isAutocomplete || isSelect) {
            const data = Object.assign({}, el.dataset) // is it necessary to use object.assign here?
            el.removeAttribute('data-row')
            el.removeAttribute('data-col')
            el.removeAttribute('data-fieldname')
            let nested = el.getElementsByTagName('input')[0]
            if (nested) {
              if (isAutocomplete) {
                nested.addEventListener('blur', (e) => handleAutocompleteBlur(e))
              }
              nested.addEventListener('focus', (e) => {
                this.$emit('updateFocus', { field: nested.name, ref, e })
                handleFocusEvent(e)
              })
              nested.setAttribute('data-row', data.row)
              nested.setAttribute('data-col', data.col)
              nested.setAttribute('data-fieldtype', isAutocomplete ? 'autocomplete' : 'select')
              nested.setAttribute('data-fieldname', data.fieldname)
            }
          } else {
            el.addEventListener('focus', () => this.$emit('updateFocus', { field: el.name, ref }))
          }
        }
      },
      /**
       * Applies fixed values to given line fields based on whether the field definition object contains
       * a 'staticValue' property. This is generally used when a field's value should be set based on a header
       * field's value. For instance, if the 'account' field should display only the account corresponding to the
       * 'default_account_deposit' property of batch's corresponding bank.
       */
      applyStaticValues () {
        this.fields.forEach(field => {
          const { name, staticValue } = field
          if (staticValue && !(this.line[name].hasOwnProperty('value') ? this.line[name].value : this.line[name])) {
            this.modifyLine(name, staticValue)
          }
        })
      },
      /**
       * Resets changes tracked at the line level
       */
      resetChanges () {
        this.changes = Object.assign({})
      },
      /**
       * Opens the DC JE associated with this line
       */
      viewDc () {
        this.$emit('viewDc', this.original.id)
      },
      /**
       * Clears the value of any fields specified in the "fieldsToClear" argument, which should be an array. This
       * method normally fires when the "fieldsToClear" prop changes (using the watcher below).
       * @param fieldsToClear
       */
      clearFields (fieldsToClear = []) {
        if (!(fieldsToClear && Array.isArray(fieldsToClear))) {
          throw new TypeError(`Invalid argument passed, expected Array, got ${typeof fieldsToClear}.`)
        }
        if (fieldsToClear.some(field => typeof field !== 'string')) {
          throw new Error(`Invalid contents in fieldsToClear argument: field to be cleared must be a string.`)
        }
        fieldsToClear.forEach(fieldToClear => {
          if (!this.line.hasOwnProperty(fieldToClear)) {
            return
          }
          if (this.line[fieldToClear].hasOwnProperty('value')) {
            this.modifyLine(fieldToClear, { label: '', value: '' })
          } else if (typeof this.line[fieldToClear] === 'boolean') {
            this.modifyLine(fieldToClear, false)
          } else {
            this.modifyLine(fieldToClear, '')
          }
        })
      },
      setOriginalLine () {
        this.originalLine = this.original
      },
      isOffsetLine (line) {
        if (this.entityType === 'ap') {
          return line.is_offset
        } else if (this.entityType === 'po') {
          return ['POSTED', 'CLOSED'].includes(this.trxStatus) && !['OPEN', ''].includes(this.line.type)
        } else {
          const matches = ['AR Offset', 'Receipt Offset', 'PETTY CASH OFFSET', 'PCARD OFFSET', 'Credit Card Offset', 'Payroll Offset', 'Payroll Clearing', 'Fringe Clearing', 'Payroll Clearing Offset', '# PAYROLL OFFSET', '# FRINGE PAYROLL OFFSET', 'PAYROLL OFFSET', 'FRINGE PAYROLL OFFSET']
          return ['POSTED', 'CLOSED'].includes(this.trxStatus) && (matches.includes(line.description) || line.description.match('PETTY CASH ADVANCE') || line.description.match('relieved'))
        }
      }
    },
    computed: {
      ...mapGetters({
        getTaxcodeById: 'getTaxcodeById',
        getFreefieldById: 'getFreefieldById',
        getVendorById: 'getVendorById',
        getPoLineById: 'getPoLineById',
        getLineById: 'getLineById',
        checkIfUserHasRequiredPermissions: 'checkIfUserHasRequiredPermissions'
      }),
      /**
       * Returns the index of the current line
       * @return {number}
       */
      lineIndex () {
        return this.line_number - 1
      },
      /**
       * Checks if line contains an "offset_by" property
       * @return {boolean}
       */
      lineIsOffsetBy () {
        if (['POSTED', 'CLOSED', 'VOIDED', 'DELETED'].includes(this.trxStatus) && this.original.original_line_id) {
          const reference = this.getLineById(capitalizeFirstLetter(this.entityType), this.original.original_line_id)
          return (reference && reference.id && !!reference.offset_by) ? reference.offset_by : null
        }
        return null
      }
    },
    watch: {
      original () {
        this.line = this.original
      },
      /**
       * Watches for changes and emits a trackChangedLines event when a change is detected
       */
      changes () {
        this.$emit('trackChangedLines', { line_number: this.line_number, changed: !!Object.keys(this.changes).length })
      },
      /**
       * Watches the fields and applies any static values that need to be applied
       */
      fields () {
        this.applyStaticValues()
      },
      /**
       * Watches "fieldsToClear" prop. If it is an array and if its length is greater than 0, calls the "clearFields"
       * method, passing in the array of fields that should be cleared.
       */
      fieldsToClear () {
        if (this.fieldsToClear && Array.isArray(this.fieldsToClear) && this.fieldsToClear.length) {
          this.clearFields(this.fieldsToClear)
        }
      }
    },
    created () {
      this.initializeLineForm()
      this.applyStaticValues()
      this.setOriginalLine()
    },
    mounted () {
      this.initializeComponent()
    },
    render () {
      const { saveErr, line_number, willBeDeleted, readOnly, lineIsOffsetBy, linesContainDcs, splitBy, trxStatus } = this
      /**
       * Renders the "Delete" button in the last column if the transaction is not posted to the GL
       */
      const deleteBtn = () => {
        if (!this.hideDeleteBtn) {
          return (
            <td width="3%;" style="padding: 0; text-align: center;">
              <el-button
                icon="delete"
                size="mini"
                disabled={(!this.line.id && (!Object.keys(this.changes).length && this.totalLines <= 20)) || readOnly}
                plain={!willBeDeleted}
                type={willBeDeleted ? 'danger' : null}
                onClick={() => this.$emit('deleteLine', line_number)} />
            </td>
          )
        }
      }
      /**
       * Renders the "View" button in the "DC" column
       */
      const viewDcBtn = () => {
        if (!linesContainDcs) {
          return null
        }
        if (linesContainDcs && !!lineIsOffsetBy) {
          return (
            <el-button size="mini" onClick={() => this.viewDc()}>
              View
            </el-button>
          )
        }
      }
      /**
       * Renders the "Split" button in the "DC" column
       */
      const splitLineBtn = () => {
        // Removed because it does not work as expected
        // return
        const setStyle = () => {
          let style = {
            'background-color': '#ffffff'
          }

          return style
        }
        const disabled = this.isOffsetLine(this.line) || !this.hasEditPermission || false
        if (this.line.id && !readOnly && !(splitBy && splitBy.length) && !Object.keys(this.changes).length && !(linesContainDcs && !!lineIsOffsetBy)) {
          return (
            <el-button disabled={disabled} style={setStyle()} size="mini" onClick={() => this.$emit('splitLine', line_number)}>
              Split
            </el-button>
          )
        }
      }
      /**
       * Renders the "DC" column that contains options related to distribution changes
       */
      const dcOptions = () => {
        if (['DRAFT', 'OPEN', 'AUDITED', 'POSTED', 'CLOSED', 'VOIDED', 'DELETED'].includes(trxStatus) && (linesContainDcs || !readOnly) && this.entityType !== 'po') {
          return (
            <td width="5%;" style="padding: 0; text-align: center; border: 1px solid lightgrey;">
              <el-button-group>
                {viewDcBtn()}
                {splitLineBtn()}
              </el-button-group>
            </td>
          )
        }
      }
      /**
       * Sets the row background color depending on whether the line failed to save or is pending deletion
       */
      const setBackground = () => {
        if (saveErr) {
          return 'palevioletred'
        } else if (willBeDeleted) {
          return 'lightgrey'
        }
        return '#fff'
      }
      return (
        <tr style={ `background: ${setBackground()};`} class="distribution-line">
          {this.generateLineFields()}
          {deleteBtn()}
          {dcOptions()}
        </tr>
      )
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/Line.vue